import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Calendar,
  User,
  FileText,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  MinusCircle,
  ExternalLink,
  Lock,
  Copy,
  PenTool,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Skeleton } from '@/app/components/ui/skeleton';
import { Separator } from '@/app/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/app/components/ui/alert-dialog';
import { toast } from '@/app/components/ui/simple-toaster';
import { useInspection, useDeleteInspection, useAmendInspection, useSignOffInspection } from '@/hooks/use-inspections';
import { useAsset } from '@/hooks/use-assets';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { FindingsList } from '@/components/inspections/findings-list';
import { PageBreadcrumb } from '@/app/components/page-breadcrumb';
import { formatDateOnly, formatDateTimeInTimeZone } from '@/lib/date-utils';
import type { InspectionResult } from '@/lib/types';
import { usePermissions } from '@/hooks/use-permissions';

const resultColors: Record<InspectionResult, string> = {
  pass: 'bg-green-100 text-green-800 border-green-200',
  fail: 'bg-red-100 text-red-800 border-red-200',
  conditional: 'bg-yellow-100 text-yellow-800 border-yellow-200',
};

const resultIcons: Record<InspectionResult, React.ReactNode> = {
  pass: <CheckCircle className="h-5 w-5" />,
  fail: <XCircle className="h-5 w-5" />,
  conditional: <AlertTriangle className="h-5 w-5" />,
};

const resultLabels: Record<InspectionResult, string> = {
  pass: 'Pass',
  fail: 'Fail',
  conditional: 'Conditional',
};

export function InspectionDetailPage() {
  const { canRecordInspections } = usePermissions();
  // Edit/delete are seeded to the same Inspection Certified people as create.
  const canEditInspections = canRecordInspections;
  const orgTimeZone = useOrgTimeZone();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const inspectionId = parseInt(id || '0', 10);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [signOffName, setSignOffName] = useState('');
  const [signOffPin, setSignOffPin] = useState('');

  const { data: inspection, isLoading, isError } = useInspection(inspectionId);
  const { data: asset, isLoading: assetLoading } = useAsset(inspection?.asset || 0);
  const deleteMutation = useDeleteInspection();
  const amendMutation = useAmendInspection();
  const signOffMutation = useSignOffInspection();

  const handleAmend = async () => {
    try {
      const amended = await amendMutation.mutateAsync({ id: inspectionId });
      toast.success('Amendment created successfully');
      navigate(`/inspections/${amended.id}`);
    } catch {
      toast.error('Failed to create amendment. Please try again.');
    }
  };

  const handleSignOff = async () => {
    if (!signOffName.trim()) {
      toast.error('Please type your full name to sign off.');
      return;
    }
    try {
      await signOffMutation.mutateAsync({
        id: inspectionId,
        data: {
          signer_name: signOffName.trim(),
          ...(signOffPin ? { pin: signOffPin } : {}),
        },
      });
      toast.success('Inspection signed off successfully');
      setSignOffName('');
      setSignOffPin('');
    } catch {
      toast.error('Failed to sign off. Check your PIN if required.');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(inspectionId);
      toast.success('Inspection deleted successfully');
      navigate('/inspections');
    } catch {
      toast.error('Failed to delete inspection. Please try again.');
    }
  };

  const formatDate = (dateString: string) => {
    return formatDateOnly(dateString, '—', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString: string) => {
    return formatDateTimeInTimeZone(dateString, orgTimeZone, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-64" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (isError || !inspection) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card>
          <CardContent className="p-12 text-center">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Inspection Not Found</h2>
            <p className="text-gray-600 mb-4">
              The inspection you're looking for doesn't exist or you don't have permission to view it.
            </p>
            <Button onClick={() => navigate('/inspections')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Inspections
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // null result = pending inspection awaiting a certified inspector.
  const result = inspection.result;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PageBreadcrumb items={[
        { label: 'Inspections', href: '/inspections' },
        { label: `Inspection — ${formatDate(inspection.date)}` },
      ]} />
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/inspections')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Inspection Details
            </h1>
            <p className="text-gray-600 mt-1">
              {formatDate(inspection.date)}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {inspection.is_locked ? (
            <Button variant="outline" onClick={handleAmend} disabled={amendMutation.isPending}>
              <Copy className="h-4 w-4 mr-2" />
              {amendMutation.isPending ? 'Amending...' : 'Amend Inspection'}
            </Button>
          ) : canEditInspections ? (
            <Button variant="outline" asChild>
              <Link to={`/inspections/${inspection.id}/edit`}>
                <Edit className="h-4 w-4 mr-2" />
                Edit Inspection
              </Link>
            </Button>
          ) : null}
          {canEditInspections && (
            <Button variant="outline" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setDeleteDialogOpen(true)}>
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Inspection Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center gap-4">
                <span className="text-gray-500">Result:</span>
                {result ? (
                  <Badge className={`${resultColors[result]} flex items-center gap-2 px-3 py-1 text-sm`}>
                    {resultIcons[result]}
                    {resultLabels[result]}
                  </Badge>
                ) : (
                  <Badge className="bg-gray-100 text-gray-800 border-gray-200 flex items-center gap-2 px-3 py-1 text-sm">
                    <Clock className="h-5 w-5" />
                    Pending
                  </Badge>
                )}
              </div>

              <Separator />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Inspection Date</p>
                    <p className="font-medium">{formatDate(inspection.date)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <User className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Inspector</p>
                    <p className="font-medium">{inspection.inspector_name || 'Not recorded'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Lock className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Record Status</p>
                    <p className="font-medium">
                      {inspection.is_locked ? 'Locked' : 'Draft'}
                      {inspection.revision > 1 && ` (Rev ${inspection.revision})`}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <FileText className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Created</p>
                    <p className="font-medium">{formatDateTime(inspection.created_at)}</p>
                  </div>
                </div>
              </div>

              {inspection.notes && (
                <>
                  <Separator />
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Notes</h3>
                    <p className="text-gray-700 whitespace-pre-wrap">{inspection.notes}</p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Per-Step Results (read-only) */}
          {inspection.step_results && inspection.step_results.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5" />
                    Inspection Steps
                  </span>
                  <Badge variant="outline">
                    {inspection.step_results.filter((s) => s.result === 'pass').length}/{inspection.step_results.length} passed
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {inspection.step_results.map((step) => (
                  <div
                    key={step.id}
                    className={`flex items-start justify-between gap-3 p-3 rounded-lg border ${
                      step.is_critical && step.result === 'fail'
                        ? 'border-red-200 bg-red-50'
                        : step.is_critical
                          ? 'border-orange-200 bg-orange-50'
                          : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-medium text-gray-500">
                          Step {step.step_number}
                        </span>
                        {step.is_critical && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                            <AlertTriangle className="h-3 w-3" />
                            Critical
                          </span>
                        )}
                      </div>
                      <p className="font-medium text-gray-900 text-sm">{step.step_title}</p>
                      {step.notes && (
                        <p className="text-xs text-gray-600 mt-1">{step.notes}</p>
                      )}
                    </div>
                    <div className="flex-shrink-0">
                      {step.result === 'pass' && (
                        <Badge className="bg-green-100 text-green-800 border-green-200">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Pass
                        </Badge>
                      )}
                      {step.result === 'fail' && (
                        <Badge className="bg-red-100 text-red-800 border-red-200">
                          <XCircle className="h-3 w-3 mr-1" />
                          Fail
                        </Badge>
                      )}
                      {step.result === 'na' && (
                        <Badge className="bg-gray-100 text-gray-800 border-gray-200">
                          <MinusCircle className="h-3 w-3 mr-1" />
                          N/A
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
                {inspection.step_results.some((s) => s.is_critical && s.result === 'fail') && (
                  <div className="flex items-center gap-2 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mt-3">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                    <span>A critical step failed — this inspection was recorded as failed.</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Findings
                </span>
                <Badge variant="outline">
                  {inspection.findings?.length || 0} finding{(inspection.findings?.length || 0) !== 1 ? 's' : ''}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <FindingsList
                findings={inspection.findings || []}
                emptyMessage="No findings were recorded for this inspection."
              />
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Asset Information</CardTitle>
            </CardHeader>
            <CardContent>
              {assetLoading ? (
                <div className="space-y-3">
                  <Skeleton className="h-6 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              ) : asset ? (
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500">Name</p>
                    <p className="font-medium">{asset.display_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Serial Number</p>
                    <p className="font-medium font-mono">{asset.serial_number}</p>
                  </div>
                  {asset.manufacturer && (
                    <div>
                      <p className="text-sm text-gray-500">Manufacturer</p>
                      <p className="font-medium">{asset.manufacturer}</p>
                    </div>
                  )}
                  {asset.length_m && (
                    <div>
                      <p className="text-sm text-gray-500">Length</p>
                      <p className="font-medium">{asset.length_m} m</p>
                    </div>
                  )}
                  <Separator />
                  <Button variant="outline" className="w-full" asChild>
                    <Link to={`/assets/${asset.id}`}>
                      <ExternalLink className="h-4 w-4 mr-2" />
                      View Asset Details
                    </Link>
                  </Button>
                </div>
              ) : (
                <p className="text-gray-500">Asset information not available</p>
              )}
            </CardContent>
          </Card>

          {/* Signature Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <PenTool className="h-5 w-5" />
                Signature
              </CardTitle>
            </CardHeader>
            <CardContent>
              {inspection.signature ? (
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-500">Signed By</p>
                    <p className="font-medium">{inspection.signature.signer_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Method</p>
                    <p className="font-medium capitalize">
                      {inspection.signature.signing_method === 'pin' ? 'PIN Re-auth' : 'Typed Name'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Signed At</p>
                    <p className="font-medium">{formatDateTime(inspection.signature.signed_at)}</p>
                  </div>
                  <Badge className="bg-green-100 text-green-800 border-green-200">
                    <Lock className="h-3 w-3 mr-1" />
                    Signed & Locked
                  </Badge>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-gray-500">
                    This inspection is not yet signed off. Sign below to lock it.
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="sign-off-name">Your Full Name *</Label>
                    <Input
                      id="sign-off-name"
                      placeholder="Type your full name"
                      value={signOffName}
                      onChange={(e) => setSignOffName(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sign-off-pin">Signing PIN (if configured)</Label>
                    <Input
                      id="sign-off-pin"
                      type="password"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="6-digit PIN"
                      value={signOffPin}
                      onChange={(e) => setSignOffPin(e.target.value)}
                    />
                  </div>
                  <Button
                    className="w-full"
                    data-validate-required="true"
                    onClick={handleSignOff}
                    disabled={signOffMutation.isPending}
                  >
                    <PenTool className="h-4 w-4 mr-2" />
                    {signOffMutation.isPending ? 'Signing...' : 'Sign Off & Lock'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {canRecordInspections && (
                <Button variant="outline" className="w-full justify-start" asChild>
                  <Link to="/inspections/new">
                    <FileText className="h-4 w-4 mr-2" />
                    New Inspection
                  </Link>
                </Button>
              )}
              {asset && (
                <Button variant="outline" className="w-full justify-start" asChild>
                  <Link to={`/inspections/new?asset=${asset.id}`}>
                    <FileText className="h-4 w-4 mr-2" />
                    Inspect This Asset Again
                  </Link>
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Inspection</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this inspection from {formatDate(inspection.date)}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Inspection'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
